// Horizontal bar list: one row per item with its label, a brand-blue bar scaled to the largest value and the count; an accessible list with the numbers in text.
import type { ReactNode } from "react";

import { cn } from "@/lib/cn";
import { formatNumber } from "@/lib/format";

export interface BarItem {
  key: string;
  label: ReactNode;
  value: number;
}

interface BarListProps {
  items: readonly BarItem[];
  ariaLabel: string;
  className?: string;
  tone?: "blue" | "ramp";
}

export function BarList({ items, ariaLabel, className, tone = "blue" }: BarListProps) {
  const max = items.reduce((highest, item) => Math.max(highest, item.value), 0);
  return (
    <ul aria-label={ariaLabel} className={cn("m-0 flex list-none flex-col gap-2.5 p-0", className)}>
      {items.map((item) => (
        <li key={item.key} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
          <span className="min-w-0 truncate text-ui text-ink-2">{item.label}</span>
          <span className="text-ui font-semibold text-foreground tabular-nums">
            {formatNumber(item.value)}
          </span>
          <span aria-hidden className="col-span-2 block h-1.5 overflow-hidden rounded-pill bg-track">
            <i
              className={cn(
                "block h-full rounded-pill",
                tone === "blue" ? "bg-brand-blue" : "bg-chart-ramp-2",
              )}
              style={{ width: max > 0 ? `${(item.value / max) * 100}%` : "0%" }}
            />
          </span>
        </li>
      ))}
    </ul>
  );
}
